/**
 * CL-SHIP-01 — the single source of truth for shipping configuration.
 *
 * Data shape (listing publicData):
 *
 *   shippingEnabled: true                            // built-in template key
 *   shippingPriceInSubunitsOneItem: 250              // built-in key, MIRROR of domestic.firstItem
 *   shippingPriceInSubunitsAdditionalItems: 50       // built-in key, MIRROR of domestic.additionalItem
 *   shippingConfig: {                                // canonical, ours
 *     v, mode, currency,
 *     origin: { stockLocationId, countryCode, region, capturedAt },
 *     domestic: { enabled, firstItem, additionalItem },
 *     international: { enabled, firstItem, additionalItem, excludedCountries },
 *     handlingTimeDays, notes, pricingSource, categoryKeyAtSave,
 *   }
 *
 * The mirror exists so the untouched template checkout keeps pricing domestic
 * orders. It is written, never read as truth — always read through
 * getShippingConfig().
 *
 * Amounts are integer currency subunits (USD cents). 0 is a valid amount and
 * means free shipping, so never use a falsy check on one.
 */

import { types as sdkTypes } from '../sdkLoader';
import { STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS } from '../types';

const { Money } = sdkTypes;

// CL-SHIP-02 moved the pure pricing rules into a dependency-free CommonJS
// module so the Express server can require the same code. Everything imported
// here is re-exported unchanged, so this file's public API is identical and
// every existing importer — ShippingInfo, EditListingDeliveryForm,
// EditListingDeliveryPanel, EditListingWizard and shipping.test.js — is
// untouched. Named imports from a CommonJS module are resolved by Babel and
// webpack interop; that is why this works in both the browser build and Jest.
import {
  FEE_AWAITING_DESTINATION,
  FEE_BLOCKED,
  FEE_NOT_OFFERED,
  FEE_NOT_PRICED,
  FEE_PRICED,
  SHIPPING_CONFIG_KEY,
  SHIPPING_MODE_FLAT,
  ZONE_DOMESTIC,
  ZONE_INTERNATIONAL,
  ZONE_UNKNOWN,
  getShippingConfig,
  isSubunitAmount,
  normalizeCountryCode,
  priceForZone,
  resolveShippingFee,
  resolveShippingZone,
} from './shippingRules';

export {
  FEE_AWAITING_DESTINATION,
  FEE_BLOCKED,
  FEE_NOT_OFFERED,
  FEE_NOT_PRICED,
  FEE_PRICED,
  SHIPPING_CONFIG_KEY,
  SHIPPING_MODE_FLAT,
  ZONE_DOMESTIC,
  ZONE_INTERNATIONAL,
  ZONE_UNKNOWN,
  getShippingConfig,
  isSubunitAmount,
  normalizeCountryCode,
  priceForZone,
  resolveShippingFee,
  resolveShippingZone,
};

export const SHIPPING_ORIGIN_PRIVATE_KEY = 'shippingOrigin';
export const SHIPPING_SCHEMA_VERSION = 1;
export const PRICING_SOURCE_MANUAL = 'manual';

export const INTERNATIONAL_YES = 'yes';
export const INTERNATIONAL_NO = 'no';

export const allowsMultipleItems = listingTypeConfig =>
  [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(listingTypeConfig?.stockType);

const amountOf = value => {
  if (value instanceof Money) return value.amount;
  return isSubunitAmount(value) ? value : null;
};

const parseHandlingTime = value => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

/**
 * The public half of a listing's origin: what goes in `shippingConfig.origin`.
 *
 * ONE builder, called by both writers — this step (when prices are saved) and
 * `buildListingLocationPatch` (when the location is saved). It was hand-written in both
 * places, so a key added to one copy would have been silently missing from the other,
 * and `getListingOrigin` reads whichever copy happened to be written last.
 *
 * Country and region only. `publicData` is world-readable, so the street address, city,
 * postcode and contact details live in privateData (SL9).
 *
 * @param {Object|null} source - a saved location record, or a stored origin
 * @param {string} capturedAt - ISO 8601 timestamp from the caller
 */
export const buildPublicOrigin = (source, capturedAt) => ({
  stockLocationId: source?.stockLocationId || source?.id || null,
  countryCode: source?.countryCode || null,
  region: source?.region || null,
  capturedAt,
});

/**
 * Build the publicData patch for the delivery step. Writes the canonical config
 * and the mirror in one object, so the two can never diverge.
 *
 * Passing null for a key removes it from the listing's extended data.
 *
 * @param {Object} formValues - the delivery form values
 * @param {string} currency
 * @param {Object} [origin] - CL-STOCK-01: { stockLocationId, countryCode, region }
 *   taken from the listing's own location copy. When absent, the origin's country
 *   stays null and `isDeliveryTabComplete` fails — which is the right answer for a
 *   listing whose location step has not been saved. There is no fallback: the
 *   `shipsFrom` autocomplete this once read was removed from the delivery step.
 */
export const buildShippingPublicData = (formValues, currency, origin = null) => {
  const {
    deliveryOptions = [],
    // `shipsFrom` was read here. The origin is now the third argument, taken from
    // the listing's own location copy (SL7, J1) — there is nothing typed to read.
    shippingPriceInSubunitsOneItem,
    shippingPriceInSubunitsAdditionalItems,
    internationalShipping,
    intlShippingPriceInSubunitsOneItem,
    intlShippingPriceInSubunitsAdditionalItems,
    handlingTimeDays,
    shippingNotes,
    categoryKeyAtSave = null,
  } = formValues || {};

  const shippingEnabled = deliveryOptions.includes('shipping');

  if (!shippingEnabled) {
    return {
      shippingEnabled: false,
      shippingPriceInSubunitsOneItem: null,
      shippingPriceInSubunitsAdditionalItems: null,
      [SHIPPING_CONFIG_KEY]: null,
    };
  }

  const domesticFirst = amountOf(shippingPriceInSubunitsOneItem);
  const domesticAdditional = amountOf(shippingPriceInSubunitsAdditionalItems);
  const internationalEnabled = internationalShipping === INTERNATIONAL_YES;

  return {
    shippingEnabled: true,
    // Mirror of the domestic pair — see the file header.
    shippingPriceInSubunitsOneItem: domesticFirst,
    shippingPriceInSubunitsAdditionalItems: domesticAdditional,
    [SHIPPING_CONFIG_KEY]: {
      v: SHIPPING_SCHEMA_VERSION,
      mode: SHIPPING_MODE_FLAT,
      currency,
      origin: buildPublicOrigin(origin, new Date().toISOString()),
      // AC10 needs to know which country these PRICES were set for, and `origin`
      // above cannot answer that: the location step rewrites `origin` whenever the
      // seller re-saves their location, so comparing it against the listing's
      // current origin always matched and the notice could never fire.
      //
      // This key is written ONLY here, by the delivery step, so it stays pinned to
      // the last time the prices were actually saved. `buildListingLocationPatch`
      // spreads the stored config, so a location change leaves it untouched.
      pricedForCountryCode: origin?.countryCode || null,
      domestic: {
        enabled: true,
        firstItem: domesticFirst,
        additionalItem: domesticAdditional,
      },
      international: {
        enabled: internationalEnabled,
        firstItem: internationalEnabled ? amountOf(intlShippingPriceInSubunitsOneItem) : null,
        additionalItem: internationalEnabled
          ? amountOf(intlShippingPriceInSubunitsAdditionalItems)
          : null,
        excludedCountries: [],
      },
      handlingTimeDays: parseHandlingTime(handlingTimeDays),
      notes: shippingNotes || null,
      pricingSource: PRICING_SOURCE_MANUAL,
      categoryKeyAtSave,
    },
  };
};

// `buildShippingPrivateData` lived here. CL-STOCK-01 deleted it: the listing's
// private origin is written once, by the location step, through
// `buildListingLocationPatch` — see util/custom/stockLocations.js. Two writers for
// one key is how a street address goes missing on an unrelated save.
// SHIPPING_ORIGIN_PRIVATE_KEY stays exported; stockLocations.js imports it.

/**
 * The publish gate for the delivery tab. Also safe to reuse server-side.
 */
export const isDeliveryTabComplete = (publicData, listingTypeConfig) => {
  const { shippingEnabled, pickupEnabled } = publicData || {};

  if (!shippingEnabled && !pickupEnabled) return false;
  if (!shippingEnabled) return true;

  const stored = publicData?.[SHIPPING_CONFIG_KEY];
  if (!stored?.v) return false;
  if (!stored.origin?.countryCode) return false;

  const requiresAdditional = allowsMultipleItems(listingTypeConfig);
  const zoneComplete = zoneConfig =>
    isSubunitAmount(zoneConfig?.firstItem) &&
    (!requiresAdditional || isSubunitAmount(zoneConfig?.additionalItem));

  if (!zoneComplete(stored.domestic)) return false;

  // The international question must have been answered either way.
  if (typeof stored.international?.enabled !== 'boolean') return false;
  if (stored.international.enabled && !zoneComplete(stored.international)) return false;

  return true;
};

/**
 * Final Form validator: block absurd amounts (a missing decimal point).
 * Non-Money values are left to the other validators.
 */
export const moneySubUnitAmountAtMost = (message, maxValue) => value => {
  if (!(value instanceof Money)) return undefined;
  return value.amount <= maxValue ? undefined : message;
};

// `originCountryRequired` lived here — the validator for the `shipsFrom`
// autocomplete CL-STOCK-01 removed from the delivery step. The country is now
// required where it is actually entered: `validateLocationDraft` in
// util/custom/stockLocations.js enforces SL1 on the location form.
